import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// IMPORTANT:
// Don't set a global 'Content-Type' header here.
// For JSON requests, we'll set 'application/json' in the interceptor below.
// For FormData (file uploads), axios needs to set 'multipart/form-data' with boundary automatically.
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Enable credentials for CORS
});

// List of endpoints that should NOT have Authorization header
const publicEndpoints = [
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/verify-reset-code',
  '/auth/reset-password',
  '/auth/resend-verification',
  '/auth/verify-email',
];

// List of endpoints that are public for GET only (read-only)
const publicGetEndpoints = [
  '/settings/', // Settings GET endpoints are public (read-only)
];

// Add token to requests (except for public endpoints)
api.interceptors.request.use(
  (config) => {
    // Check if this is a public endpoint
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      config.url?.includes(endpoint)
    );
    
    // Check if this is a public GET endpoint
    const isPublicGetEndpoint = publicGetEndpoints.some(endpoint => 
      config.url?.includes(endpoint) && config.method?.toLowerCase() === 'get'
    );
    
    // Only add token if it's not a public endpoint or public GET endpoint
    if (!isPublicEndpoint && !isPublicGetEndpoint) {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Ensure Content-Type is set for POST/PUT requests
    // BUT: Don't override if it's FormData (axios will set it automatically with boundary)
    if ((config.method === 'post' || config.method === 'put' || config.method === 'patch') 
        && !config.headers['Content-Type'] 
        && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    // Remove Content-Type if it's explicitly set to undefined (for FormData)
    if (config.headers['Content-Type'] === undefined) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if this is a public endpoint
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      error.config?.url?.includes(endpoint)
    );
    
    // Only handle token errors for protected endpoints
    // Don't clear token for public endpoints or settings GET requests
    const isSettingsGet = error.config?.url?.includes('/settings/') && error.config?.method === 'get';
    if (!isPublicEndpoint && !isSettingsGet && (error.response?.status === 401 || error.response?.status === 422)) {
      // Invalid or expired token
      localStorage.removeItem('access_token');
      // Only redirect if not already on login/register page
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        // Don't redirect, just clear token silently
      }
    }
    return Promise.reject(error);
  }
);

export default api;

